import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { Injectable } from '@angular/core';

import { FIRST_PARTY_CONFIG, type IFirstPartyConfig } from './first-party.config';

// Пара «сессия + признак новизны»: tracker шлёт session_start ровно один раз на сессию.
export interface ISessionState {
    readonly sessionId: string;
    readonly isNew: boolean;
}

const VISITOR_KEY: string = 'sc_vid';
const SESSION_KEY: string = 'sc_sid';
const SESSION_AT_KEY: string = 'sc_sid_at';
const MS_PER_DAY: number = 24 * 60 * 60 * 1000;
const UUID_BYTES: number = 16;

/**
 * A version 4 UUID from 16 random bytes. `crypto.randomUUID` exists only on https and localhost and
 * not before iOS 15.4: its absence threw while the app root was created and left the page dead
 */
export function uuidFromBytes(random: Uint8Array): string {
    const bytes: Uint8Array = Uint8Array.from(random);
    // The version nibble is 4, the variant bits are 10
    bytes[6] = 0x40 + (bytes[6] % 0x10);
    bytes[8] = 0x80 + (bytes[8] % 0x40);
    const hex: string = Array.from(bytes, (byte: number): string => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Первопартийная идентичность: `visitor_id` живёт между визитами (localStorage),
 * `session_id` — в пределах окна активности (30 мин по умолчанию) и одних суток UTC.
 * Оба генерятся на клиенте (crypto), сервер их не выдаёт и не связывает с личностью —
 * PII здесь нет. SSR-safe: на сервере возвращает пустые значения, ничего не пишет.
 */
@Injectable({ providedIn: 'root' })
export class VisitorIdentityService {
    readonly #isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
    readonly #config: IFirstPartyConfig = inject(FIRST_PARTY_CONFIG);

    public visitorId(): string {
        if (!this.#isBrowser) {
            return '';
        }
        const existing: string | null = this.#read(VISITOR_KEY);
        if (existing) {
            return existing;
        }
        const created: string = this.#uuid();
        this.#write(VISITOR_KEY, created);
        return created;
    }

    // Возвращает актуальную сессию, при необходимости открывая новую (простой сверх окна
    // активности или смена суток UTC). Каждый вызов продлевает окно активности.
    public ensureSession(): ISessionState {
        if (!this.#isBrowser) {
            return { sessionId: '', isNew: false };
        }
        const now: number = Date.now();
        const current: string | null = this.#read(SESSION_KEY);
        const lastAt: number = Number.parseInt(this.#read(SESSION_AT_KEY) ?? '', 10);
        const isNew: boolean = !current || !this.#isSameWindow(lastAt, now);
        const sessionId: string = isNew || !current ? this.#uuid() : current;
        if (isNew) {
            this.#write(SESSION_KEY, sessionId);
        }
        this.#write(SESSION_AT_KEY, String(now));
        return { sessionId, isNew };
    }

    #isSameWindow(lastAt: number, now: number): boolean {
        if (!Number.isFinite(lastAt)) {
            return false;
        }
        const withinIdle: boolean = now - lastAt <= this.#config.sessionIdleMs;
        const sameUtcDay: boolean = Math.floor(lastAt / MS_PER_DAY) === Math.floor(now / MS_PER_DAY);
        return withinIdle && sameUtcDay;
    }

    #uuid(): string {
        return uuidFromBytes(globalThis.crypto.getRandomValues(new Uint8Array(UUID_BYTES)));
    }

    #read(key: string): string | null {
        try {
            return globalThis.localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    #write(key: string, value: string): void {
        try {
            globalThis.localStorage.setItem(key, value);
        } catch {
            // приватный режим / переполнение — идентичность деградирует до in-memory на визит.
        }
    }
}
